import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import PortraitsGallery from '../../../PortraitsGallery';
import { useCustomer } from '../../../../contexts/CustomerContext';
import useSeniorBackground from '../../../../hooks/useSeniorBackground';
import { OrderPackage, Option } from '../../../../models';
import QueryKeys from '../../../../utils/QueryKeys';
import yearbookBand from './yearbookBand';

const DRESS_CODE_NOTE = 'Your yearbook photo must be formal attire and meet your school’s Senior YB '
  + 'dress code. Cap & gown photos are not accepted.';

// Identify the package's $0 "yearbook" Option the same way the rest of the senior flow
// does (senior-portrait-option-item): the Option whose name includes "yearbook".
const isYearbookOption = (option) => option?.name?.toLowerCase().includes('yearbook');

/**
 * The senior flavour of PortraitsGallery: the shared gallery plus the two things that are
 * only true of seniors — they're shot as cutouts and printed on the SCHOOL's senior
 * backdrop, and one photo has to be nominated as the official yearbook pose.
 *
 * Everything universal (grid, favorites, ordering, frame IDs, continue bar) lives in
 * PortraitsGallery, so the elementary/underclass flows can adopt the same screen by
 * simply not passing a `yearbook` capability.
 */
export default function SeniorPortraitsGallery({
  poses, backgrounds, packageId, onBuildPackage,
}) {
  const { customer } = useCustomer();
  const queryClient = useQueryClient();
  // Seniors are cutouts; preview them on the backdrop the yearbook will actually print
  // (schools.senior_background — "Senior_Blue" at Enochs).
  const { data: seniorBackgroundUrl } = useSeniorBackground(customer?.currentSchool?.id);

  const [saving, setSaving] = useState(false);

  const studentId = customer?.currentStudent?.id;
  const favoritesKey = studentId && packageId ? `senior-favorites:${studentId}:${packageId}` : null;

  // Server truth for the yearbook pose (Pose#is_yearbook_pose).
  const yearbookPose = useMemo(
    () => (poses || []).find((pose) => pose.isYearbookPose === true),
    [poses],
  );
  const yearbookId = yearbookPose?.id || null;

  // The garment classifier's verdict. Fail-open on null (unclassified is pickable), and a
  // pose already saved as the yearbook pose stays eligible so an existing selection is
  // never orphaned by a later re-classification.
  const isEligible = (pose) => pose.yearbookAcceptable !== false || pose.id === yearbookId;

  // Add the required $0 yearbook OrderPackage (no sheets), idempotently, so the yearbook
  // line is in the cart once a pose is chosen.
  const upsertYearbookOrderPackage = async (poseId) => {
    const cartId = customer?.cart?.id;
    if (!cartId) return;
    const options = (await Option.where({ packageId }).includes(['image_types']).all()).data;
    const yearbookOption = options.find(isYearbookOption);
    if (!yearbookOption) return;
    const existing = (await OrderPackage
      .where({ cartId, optionId: yearbookOption.id, studentId })
      .per(1).all()).data;
    if (existing.length > 0) return;

    const orderPackage = new OrderPackage();
    orderPackage.cartId = cartId;
    orderPackage.studentId = studentId;
    orderPackage.optionId = yearbookOption.id;
    orderPackage.packageId = packageId;
    orderPackage.apiOption = yearbookOption;
    orderPackage.seniorImageId = poseId;
    await orderPackage.save();
  };

  // Resolves true when the pick is saved — the gallery then favorites it. The eligibility
  // check is enforced server-side too (POST select_yearbook 422s a cap-and-gown pose).
  const selectYearbook = async (pose) => {
    if (!isEligible(pose)) {
      alert(DRESS_CODE_NOTE);
      return false;
    }
    if (saving) return false;
    setSaving(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      await axios.post(
        '/api/senior_images/select_yearbook',
        { pose_id: pose.id },
        { baseURL: '/', headers: { 'X-CSRF-Token': csrfToken } },
      );
      await upsertYearbookOrderPackage(pose.id);
      // Refetch so is_yearbook_pose (and the banner/step) update.
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.STUDENT_IMAGES] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.cart, QueryKeys.numItems] });
      return true;
    } catch (error) {
      alert(error?.response?.data?.error || 'Something went wrong setting your yearbook photo. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Memoized so the gallery's ordering memo doesn't recompute on every render. Rebuilt
  // whenever anything the closures read changes (the saved pose, the in-flight guard, the
  // poses themselves, the cart the $0 line is added to) — a stale closure here would
  // silently double-post or gate against last render's verdicts.
  const yearbook = useMemo(() => ({
    selectedPoseId: yearbookId,
    isEligible,
    // 3-way ordering key: yearbook-formal -> cap & gown -> other (nil rides with formal).
    bandOf: (pose) => yearbookBand(pose, yearbookId),
    onSelect: selectYearbook,
    note: DRESS_CODE_NOTE,
  }), [yearbookId, saving, poses, customer?.cart?.id]);

  return (
    <PortraitsGallery
      poses={poses}
      backgrounds={backgrounds}
      packageId={packageId}
      headline={`${customer.currentStudent.firstName}'s Senior Portraits`}
      backdropUrl={seniorBackgroundUrl}
      yearbook={yearbook}
      favoritesKey={favoritesKey}
      onContinue={onBuildPackage}
      // Keeps this screen on the pre-redesign card / header / banner. The elementary
      // gallery was reworked in Favorites_Page_080826; seniors were deliberately left
      // alone, so this flag is what freezes them.
      senior
    />
  );
}

SeniorPortraitsGallery.defaultProps = {
  poses: [],
  backgrounds: [],
  onBuildPackage: () => {},
};

SeniorPortraitsGallery.propTypes = {
  // Spraypaint Pose models — objects, not a fixed shape.
  // eslint-disable-next-line react/forbid-prop-types
  poses: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  backgrounds: PropTypes.arrayOf(PropTypes.object),
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onBuildPackage: PropTypes.func,
};
